import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Eye, EyeOff, Mail, Lock, ArrowRight, KeyRound, ArrowLeft, User, Phone, Building, Building2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { areFunctionalCookiesAllowed, setFunctionalStorage } from '../common/CookieConsent';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = 'willkart_partner_remembered_email';

const LoginModal = ({ isOpen, onClose, initialStep = 'email' }) => {
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const passwordInputRef = useRef(null);
  // Steps: 'audience' | 'client_subdomain' | 'email' | 'password' | 'set_password' | 'forgot_password' | 'reset_password' | 'register_partner'
  const [loginStep, setLoginStep] = useState(initialStep);
  const [userType, setUserType] = useState(null); // 'admin' | 'partner'
  const [partnerData, setPartnerData] = useState(null);
  const [resetToken, setResetToken] = useState(null);
  // Client subdomain input (default to "onetreehill" demo partner)
  const [clientSubdomain, setClientSubdomain] = useState('onetreehill');
  
  // Initialize form data with remembered email (if functional cookies are allowed)
  const [formData, setFormData] = useState(() => {
    const savedEmail = areFunctionalCookiesAllowed() ? localStorage.getItem(STORAGE_KEY) : null;
    return {
      email: savedEmail || '',
      password: '',
      confirmPassword: '',
      name: '',
      mobile: '',
      companyName: '',
    };
  });
  
  // Initialize returning user state
  const [isReturningUser, setIsReturningUser] = useState(() => {
    return areFunctionalCookiesAllowed() && !!localStorage.getItem(STORAGE_KEY);
  });

  // Re-check for remembered email when modal opens
  const prevIsOpen = useRef(false);
  useEffect(() => {
    if (isOpen && !prevIsOpen.current) {
      // Sync the entry step to whatever the caller requested (e.g. 'audience' for Get Started)
      setLoginStep(initialStep);
      const savedEmail = localStorage.getItem(STORAGE_KEY);
      if (savedEmail && !formData.email) {
        setFormData(prev => ({ ...prev, email: savedEmail }));
        setIsReturningUser(true);
      }
    }
    prevIsOpen.current = isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialStep]);

  // Auto-focus password when returning user goes to password step
  useEffect(() => {
    if (isReturningUser && loginStep === 'password') {
      setTimeout(() => {
        passwordInputRef.current?.focus();
      }, 100);
    }
  }, [isReturningUser, loginStep]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetModal = () => {
    setFormData({ email: '', password: '', confirmPassword: '', name: '', mobile: '', companyName: '' });
    setLoginStep(initialStep);
    setUserType(null);
    setPartnerData(null);
    setResetToken(null);
    setIsReturningUser(false);
    setClientSubdomain('onetreehill');
    // Re-check for remembered email
    const savedEmail = localStorage.getItem(STORAGE_KEY);
    if (savedEmail) {
      setFormData(prev => ({ ...prev, email: savedEmail }));
      setIsReturningUser(true);
    }
  };

  // Handle switching accounts (clear remembered email)
  const handleSwitchAccount = () => {
    localStorage.removeItem(STORAGE_KEY);
    setFormData({ email: '', password: '', confirmPassword: '', name: '', mobile: '', companyName: '' });
    setIsReturningUser(false);
    setLoginStep('email');
    setUserType(null);
    setPartnerData(null);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const email = formData.email.trim().toLowerCase();

    // Check if it's admin email
    try {
      // Try to check partner first
      const partnerResponse = await axios.post(`${API}/partner/check-email`, { email });
      
      if (partnerResponse.data.exists) {
        setUserType('partner');
        setPartnerData(partnerResponse.data.partner);
        
        if (partnerResponse.data.requires_password_setup) {
          // First time login - need to set password
          setLoginStep('set_password');
          toast.info(`Welcome ${partnerResponse.data.partner.name}! Please set your password.`);
        } else {
          // Returning user - show password input
          setLoginStep('password');
        }
        setIsLoading(false);
        return;
      }
    } catch (error) {
      // Not a partner, might be admin
    }

    // Check if admin (we'll try admin login with empty password to check)
    try {
      const adminCheckResponse = await axios.post(`${API}/admin/check-email`, { email });
      if (adminCheckResponse.data.is_admin) {
        setUserType('admin');
        setLoginStep('password');
        setIsLoading(false);
        return;
      }
    } catch (error) {
      // Not admin either
    }

    // Email not found - offer partner registration
    if (userType === 'partner' || userType === null) {
      // Store email and offer registration
      setUserType('partner');
      setLoginStep('register_partner');
      toast.info('No account found. Register as a new partner.');
    } else {
      toast.error('No account found with this email');
    }
    setIsLoading(false);
  };

  const handlePartnerRegister = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    if (!formData.name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    
    if (!formData.mobile.trim()) {
      toast.error('Please enter your mobile number');
      return;
    }

    if (!/^\d{10}$/.test(formData.mobile.trim().replace(/[\s\-\+]/g, '').slice(-10))) {
      toast.error('Mobile number must be 10 digits');
      return;
    }

    if (!/^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/.test(formData.email.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (!termsAccepted) {
      toast.error('Please accept the Terms & Conditions and Privacy Policy');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/partner/register`, {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        mobile: formData.mobile.trim(),
        password: formData.password,
        company_name: formData.companyName.trim() || null,
      });
      
      // Check if email verification is required (walk-in partner)
      if (response.data.requires_email_verification) {
        setLoginStep('verification_pending');
        setPartnerData({
          email: response.data.email,
          name: response.data.partner_name
        });
        toast.success('Registration successful! Please check your email to verify your account.');
      } else if (response.data.token) {
        // Fallback for non-walk-in flow (shouldn't happen but just in case)
        localStorage.setItem('partnerToken', response.data.token);

        if (response.data.refresh_token) {

          localStorage.setItem('partnerAccessToken', response.data.access_token || response.data.token);

          localStorage.setItem('partnerRefreshToken', response.data.refresh_token);

        }
        localStorage.setItem('partnerEmail', response.data.partner.email);
        localStorage.setItem('partnerId', response.data.partner.id);
        localStorage.setItem('partnerName', response.data.partner.name);
        localStorage.setItem('partnerStatus', response.data.partner.status);
        
        toast.success(response.data.message);
        handleClose();
        navigate('/partner/dashboard');
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = Array.isArray(detail) ? detail.map(e => e.msg?.replace('Value error, ', '')).join(', ') : detail || 'Registration failed';
      toast.error(message);
    }
    
    setIsLoading(false);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (userType === 'admin') {
      try {
        const response = await axios.post(`${API}/admin/login`, {
          email: formData.email,
          password: formData.password,
        });

        if (response.data.token) {
          // Save email for next visit (respects cookie preferences)
          setFunctionalStorage(STORAGE_KEY, formData.email);
          localStorage.setItem('adminToken', response.data.token);

          if (response.data.refresh_token) {

            localStorage.setItem('adminAccessToken', response.data.access_token || response.data.token);

            localStorage.setItem('adminRefreshToken', response.data.refresh_token);

          }
          localStorage.setItem('adminEmail', formData.email);
          toast.success('Welcome to Admin Dashboard!');
          handleClose();
          navigate('/admin/dashboard');
        }
      } catch (error) {
        toast.error('Invalid password');
      }
    } else if (userType === 'partner') {
      try {
        const response = await axios.post(`${API}/partner/login`, {
          email: formData.email,
          password: formData.password,
        });

        // Check if email verification is required
        if (response.data.requires_email_verification) {
          setLoginStep('verification_pending');
          setPartnerData({
            email: response.data.email,
            name: response.data.partner_name
          });
          toast.info('Please verify your email before logging in.');
        } else if (response.data.token) {
          // Save email for next visit (respects cookie preferences)
          setFunctionalStorage(STORAGE_KEY, formData.email);
          localStorage.setItem('partnerToken', response.data.token);

          if (response.data.refresh_token) {

            localStorage.setItem('partnerAccessToken', response.data.access_token || response.data.token);

            localStorage.setItem('partnerRefreshToken', response.data.refresh_token);

          }
          localStorage.setItem('partnerEmail', formData.email);
          localStorage.setItem('partnerId', response.data.partner.id);
          localStorage.setItem('partnerName', response.data.partner.name);
          sessionStorage.setItem('partner_just_logged_in', 'true');
          toast.success('Welcome to Partner Dashboard!');
          handleClose();
          navigate('/partner/dashboard');
        }
      } catch (error) {
        toast.error('Invalid password');
      }
    }

    setIsLoading(false);
  };

  const handleSetPasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    if (!termsAccepted) {
      toast.error('Please accept the Terms & Conditions and Privacy Policy');
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/partner/set-password`, {
        email: partnerData.email,
        password: formData.password,
      });

      if (response.data.token) {
        // Save email for next visit (respects cookie preferences)
        setFunctionalStorage(STORAGE_KEY, partnerData.email);
        localStorage.setItem('partnerToken', response.data.token);

        if (response.data.refresh_token) {

          localStorage.setItem('partnerAccessToken', response.data.access_token || response.data.token);

          localStorage.setItem('partnerRefreshToken', response.data.refresh_token);

        }
        localStorage.setItem('partnerEmail', partnerData.email);
        localStorage.setItem('partnerId', partnerData.id);
        localStorage.setItem('partnerName', partnerData.name);
        sessionStorage.setItem('partner_just_logged_in', 'true');
        toast.success('Password set successfully! Welcome to Partner Dashboard!');
        handleClose();
        navigate('/partner/dashboard');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to set password';
      toast.error(message);
    }

    setIsLoading(false);
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.post(`${API}/partner/forgot-password`, {
        email: formData.email,
      });

      // Show success message - email will be sent with magic link
      toast.success('If an account exists with this email, you will receive a password reset link shortly.');
      setLoginStep('email'); // Go back to email step
      setFormData((prev) => ({ ...prev, email: '', password: '', confirmPassword: '' }));
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to process request. Please try again.';
      toast.error(message);
    }

    setIsLoading(false);
  };

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/partner/reset-password`, {
        reset_token: resetToken,
        new_password: formData.password,
      });

      if (response.data.token) {
        localStorage.setItem('partnerToken', response.data.token);

        if (response.data.refresh_token) {

          localStorage.setItem('partnerAccessToken', response.data.access_token || response.data.token);

          localStorage.setItem('partnerRefreshToken', response.data.refresh_token);

        }
        localStorage.setItem('partnerEmail', partnerData.email);
        localStorage.setItem('partnerId', response.data.partner.id);
        localStorage.setItem('partnerName', response.data.partner.name);
        sessionStorage.setItem('partner_just_logged_in', 'true');
        toast.success('Password reset successfully! Welcome back!');
        handleClose();
        navigate('/partner/dashboard');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to reset password';
      toast.error(message);
    }

    setIsLoading(false);
  };

  const handleResendVerification = async () => {
    if (!partnerData?.email) {
      toast.error('No email found. Please try logging in again.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/partner/resend-verification`, {
        email: partnerData.email,
      });

      toast.success(response.data.message || 'Verification email sent!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to resend verification email';
      toast.error(message);
    }

    setIsLoading(false);
  };

  const handleClientSubdomainSubmit = async (e) => {
    e.preventDefault();
    const subdomain = clientSubdomain.toLowerCase().trim().replace(/[^a-z0-9-]/g, '');
    if (!subdomain) {
      toast.error('Please enter your partner name');
      return;
    }
    setIsLoading(true);
    try {
      const { data } = await axios.get(`${API}/validate-subdomain/${subdomain}`);
      if (data.valid && data.portal_url) {
        toast.success(`Redirecting to ${subdomain}.willkart.ai...`);
        window.location.href = data.portal_url;
      } else if (data.reason === 'disabled') {
        toast.error('This partner portal is currently disabled. Please contact your partner.');
        setIsLoading(false);
      } else {
        toast.error(`We couldn't find a partner named "${subdomain}". Please check the name with your partner.`);
        setIsLoading(false);
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    switch (loginStep) {
      case 'audience':
        // Audience step has no form submission - handled by card buttons
        break;
      case 'client_subdomain':
        handleClientSubdomainSubmit(e);
        break;
      case 'email':
        handleEmailSubmit(e);
        break;
      case 'password':
        handlePasswordSubmit(e);
        break;
      case 'set_password':
        handleSetPasswordSubmit(e);
        break;
      case 'forgot_password':
        handleForgotPasswordSubmit(e);
        break;
      case 'reset_password':
        handleResetPasswordSubmit(e);
        break;
      case 'register_partner':
        handlePartnerRegister(e);
        break;
      default:
        break;
    }
  };

  const getTitle = () => {
    switch (loginStep) {
      case 'audience':
        return 'Welcome to WillKart';
      case 'client_subdomain':
        return 'Access Your Client Portal';
      case 'email':
        return 'Welcome Partners';
      case 'password':
        return 'Welcome Back';
      case 'set_password':
        return 'Set Your Password';
      case 'forgot_password':
        return 'Forgot Password';
      case 'reset_password':
        return 'Reset Password';
      case 'register_partner':
        return 'Partner Registration';
      case 'verification_pending':
        return 'Verify Your Email';
      default:
        return 'Welcome Partners';
    }
  };

  const getSubtitle = () => {
    switch (loginStep) {
      case 'audience':
        return 'Tell us a little about yourself to get started';
      case 'client_subdomain':
        return "Existing customers log in via their partner's portal";
      case 'email':
        return 'Login / Register';
      case 'password':
        return partnerData?.name ? `Welcome back, ${partnerData.name}` : formData.email;
      case 'set_password':
        return `Welcome ${partnerData?.name}! Create a password to secure your account.`;
      case 'forgot_password':
        return 'Enter your email to reset your password';
      case 'reset_password':
        return `Hi ${partnerData?.name}! Create your new password.`;
      case 'register_partner':
        return 'Create your partner account to get started';
      case 'verification_pending':
        return `We've sent a verification link to ${partnerData?.email || 'your email'}`;
      default:
        return '';
    }
  };

  const getButtonText = () => {
    if (isLoading) {
      switch (loginStep) {
        case 'client_subdomain':
          return 'Verifying...';
        case 'email':
          return 'Checking...';
        case 'password':
          return 'Signing in...';
        case 'set_password':
          return 'Setting password...';
        case 'forgot_password':
          return 'Verifying...';
        case 'reset_password':
          return 'Resetting password...';
        case 'register_partner':
          return 'Creating account...';
        default:
          return 'Loading...';
      }
    }

    switch (loginStep) {
      case 'client_subdomain':
        return 'Go to my Portal';
      case 'email':
        return 'Continue';
      case 'password':
        return 'Sign In';
      case 'set_password':
        return 'Set Password & Continue';
      case 'forgot_password':
        return 'Continue';
      case 'reset_password':
        return 'Reset Password';
      case 'register_partner':
        return 'Create Partner Account';
      default:
        return 'Continue';
    }
  };

  const inputClass = "pl-10 pr-10 bg-slate-800/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-amber-500 focus:ring-amber-500/20";

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className={`bg-slate-900 border-slate-700 text-white max-w-md ${loginStep === 'register_partner' ? 'max-h-[90vh] overflow-y-auto' : ''}`}>
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center">
            {getTitle()}
          </DialogTitle>
          <p className="text-slate-400 text-center text-sm mt-2">
            {getSubtitle()}
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className={`mt-6 ${loginStep === 'register_partner' ? 'space-y-3' : 'space-y-5'}`} data-testid="login-form">
          
          {/* Step 0: Audience Picker (Partner vs Client) */}
          {loginStep === 'audience' && (
            <div className="space-y-3" data-testid="audience-picker">
              <button
                type="button"
                onClick={() => setLoginStep('client_subdomain')}
                className="w-full text-left p-4 rounded-xl border border-slate-700 bg-slate-800/40 hover:bg-slate-800/80 hover:border-amber-500/60 transition-all duration-200 group"
                data-testid="audience-client-btn"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-amber-500/15 flex items-center justify-center flex-shrink-0 group-hover:bg-amber-500/25 transition-colors">
                    <User className="w-6 h-6 text-amber-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-semibold">I'm a Customer</p>
                    <p className="text-slate-400 text-sm mt-0.5">I was referred by a partner to create my will</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-500 group-hover:text-amber-400 group-hover:translate-x-1 transition-all" />
                </div>
              </button>

              <button
                type="button"
                onClick={() => setLoginStep('email')}
                className="w-full text-left p-4 rounded-xl border border-slate-700 bg-slate-800/40 hover:bg-slate-800/80 hover:border-amber-500/60 transition-all duration-200 group"
                data-testid="audience-partner-btn"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-amber-500/15 flex items-center justify-center flex-shrink-0 group-hover:bg-amber-500/25 transition-colors">
                    <Building2 className="w-6 h-6 text-amber-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-semibold">I'm a Partner</p>
                    <p className="text-slate-400 text-sm mt-0.5">I'm a firm or advisor managing clients on WillKart</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-500 group-hover:text-amber-400 group-hover:translate-x-1 transition-all" />
                </div>
              </button>

              <p className="text-slate-500 text-xs text-center pt-2">
                Not sure which? Customers were referred by a firm or advisor.
                Partners run the platform on behalf of customers.
              </p>
            </div>
          )}

          {/* Step 0b: Client Portal Subdomain Input */}
          {loginStep === 'client_subdomain' && (
            <>
              <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                <div className="flex items-start gap-3">
                  <Building2 className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-white font-medium text-sm">Enter your partner's name</p>
                    <p className="text-slate-400 text-xs mt-1">
                      This is the name of the firm or advisor who set up your will.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="clientSubdomain" className="text-slate-300 mb-2 block">
                  Partner Name
                </Label>
                <div className="relative flex items-center">
                  <Input
                    id="clientSubdomain"
                    name="clientSubdomain"
                    type="text"
                    required
                    autoFocus
                    value={clientSubdomain}
                    onChange={(e) => setClientSubdomain(e.target.value)}
                    placeholder="e.g. onetreehill"
                    className="pr-32 bg-slate-800/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-amber-500 focus:ring-amber-500/20"
                    data-testid="client-subdomain-input"
                  />
                  <span className="absolute right-3 text-slate-500 text-sm pointer-events-none select-none">
                    .willkart.ai
                  </span>
                </div>
                <p className="text-slate-500 text-xs mt-2">
                  You will be redirected to{' '}
                  <span className="text-amber-400">
                    {(clientSubdomain.toLowerCase().trim().replace(/[^a-z0-9-]/g, '')) || 'your-partner'}.willkart.ai
                  </span>
                </p>
              </div>

              {initialStep === 'audience' && (
                <button
                  type="button"
                  onClick={() => setLoginStep('audience')}
                  className="text-sm text-amber-400 hover:text-amber-300 transition-colors flex items-center gap-1"
                  data-testid="client-subdomain-back-btn"
                >
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
              )}

              <div className="pt-2 border-t border-slate-800">
                <p className="text-slate-500 text-xs text-center flex items-center justify-center gap-1.5">
                  <ExternalLink className="w-3 h-3" />
                  Don't know your partner? Contact the firm or advisor that helped you create your will.
                </p>
              </div>
            </>
          )}

          {/* Step 1: Email Input */}
          {loginStep === 'email' && (
            <div>
              {isReturningUser ? (
                <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                  <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <span className="text-amber-400 font-semibold">
                      {formData.email.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm">Welcome back</p>
                    <p className="text-slate-400 text-sm truncate">{formData.email}</p>
                  </div>
                  <button
                    type="button"
                    onClick={handleSwitchAccount}
                    className="text-amber-400 hover:text-amber-300 text-sm whitespace-nowrap"
                    data-testid="partner-switch-account-btn"
                  >
                    Not you?
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email"
                    className={inputClass}
                    data-testid="login-email-input"
                    autoFocus
                  />
                </div>
              )}
            </div>
          )}

          {/* Step 2: Password Input (Returning User) */}
          {loginStep === 'password' && (
            <>
              <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <span className="text-amber-400 font-semibold">
                    {(partnerData?.name || formData.email).charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium truncate">{partnerData?.name || 'Admin'}</p>
                  <p className="text-slate-400 text-sm truncate">{formData.email}</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setLoginStep('email');
                    setUserType(null);
                    setPartnerData(null);
                    setFormData((prev) => ({ ...prev, password: '' }));
                  }}
                  className="text-slate-400 hover:text-white text-sm"
                >
                  Change
                </button>
              </div>

              <div>
                <Label htmlFor="password" className="text-slate-300 mb-2 block">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    ref={passwordInputRef}
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    autoComplete="current-password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter your password"
                    className={inputClass}
                    data-testid="login-password-input"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {userType === 'partner' && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setLoginStep('forgot_password')}
                    className="text-sm text-amber-400 hover:text-amber-300 transition-colors"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </button>
                </div>
              )}
            </>
          )}

          {/* Step: Set Password (First Time User) */}
          {loginStep === 'set_password' && (
            <>
              <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                <div className="flex items-center gap-3">
                  <KeyRound className="w-5 h-5 text-amber-400" />
                  <div>
                    <p className="text-white font-medium">First Time Login</p>
                    <p className="text-slate-400 text-sm">Create a secure password to access your dashboard</p>
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="text-slate-300 mb-2 block">
                  New Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    autoComplete="new-password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Create a password (min 8 characters)"
                    className={inputClass}
                    data-testid="set-password-input"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="text-slate-300 mb-2 block">
                  Confirm Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm your password"
                    className={inputClass}
                    data-testid="confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Terms & Conditions Checkbox */}
              <div className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                <Checkbox
                  id="partner-terms-checkbox"
                  checked={termsAccepted}
                  onCheckedChange={setTermsAccepted}
                  className="mt-0.5 border-slate-500 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                  data-testid="partner-terms-checkbox"
                />
                <label htmlFor="partner-terms-checkbox" className="text-sm text-slate-300 cursor-pointer select-none">
                  By clicking Create Account, you agree to our{' '}
                  <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300 underline">
                    Terms & Conditions
                  </a>
                  {' '}&{' '}
                  <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300 underline">
                    Privacy Policy
                  </a>
                </label>
              </div>

              <button
                type="button"
                onClick={() => {
                  setLoginStep('email');
                  setFormData({ email: '', password: '', confirmPassword: '' });
                  setPartnerData(null);
                  setTermsAccepted(false);
                }}
                className="text-sm text-amber-400 hover:text-amber-300 transition-colors flex items-center gap-1"
              >
                <ArrowLeft className="w-4 h-4" /> Back to login
              </button>
            </>
          )}

          {/* Step: Forgot Password */}
          {loginStep === 'forgot_password' && (
            <>
              <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-blue-400" />
                  <div>
                    <p className="text-white font-medium">Password Recovery</p>
                    <p className="text-slate-400 text-sm">Enter your registered email address</p>
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="email" className="text-slate-300 mb-2 block">
                  Email Address
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your registered email"
                    className={inputClass}
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  setLoginStep('password');
                  setFormData((prev) => ({ ...prev, password: '' }));
                }}
                className="text-sm text-amber-400 hover:text-amber-300 transition-colors flex items-center gap-1"
              >
                <ArrowLeft className="w-4 h-4" /> Back to login
              </button>
            </>
          )}

          {/* Step: Reset Password */}
          {loginStep === 'reset_password' && (
            <>
              <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                <div className="flex items-center gap-3">
                  <KeyRound className="w-5 h-5 text-emerald-400" />
                  <div>
                    <p className="text-white font-medium">Create New Password</p>
                    <p className="text-slate-400 text-sm">Choose a strong password for your account</p>
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="text-slate-300 mb-2 block">
                  New Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Create a password (min 8 characters)"
                    className={inputClass}
                    data-testid="reset-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="text-slate-300 mb-2 block">
                  Confirm New Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm your password"
                    className={inputClass}
                    data-testid="reset-confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  setLoginStep('email');
                  setFormData({ email: '', password: '', confirmPassword: '', name: '', mobile: '', companyName: '' });
                  setResetToken(null);
                  setPartnerData(null);
                }}
                className="text-sm text-amber-400 hover:text-amber-300 transition-colors flex items-center gap-1"
              >
                <ArrowLeft className="w-4 h-4" /> Back to login
              </button>
            </>
          )}

          {/* Step: Partner Registration */}
          {loginStep === 'register_partner' && (
            <>
              <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                <div className="flex items-center gap-3">
                  <Building className="w-5 h-5 text-emerald-400" />
                  <div>
                    <p className="text-white font-medium text-sm">Become a Partner</p>
                    <p className="text-slate-400 text-xs">Create your account to start managing clients</p>
                  </div>
                </div>
              </div>

              {/* Email (read-only) */}
              <div>
                <Label htmlFor="email" className="text-slate-300 mb-1 block text-sm">
                  Email Address
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    readOnly
                    className={`${inputClass} opacity-60 h-9 text-sm`}
                  />
                </div>
              </div>

              {/* Name */}
              <div>
                <Label htmlFor="name" className="text-slate-300 mb-1 block text-sm">
                  Your Name <span className="text-red-400">*</span>
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    className={`${inputClass} h-9 text-sm`}
                    data-testid="register-name-input"
                    autoFocus
                  />
                </div>
              </div>

              {/* Company Name (optional) */}
              <div>
                <Label htmlFor="companyName" className="text-slate-300 mb-1 block text-sm">
                  Company/Firm Name <span className="text-slate-500 text-xs">(optional)</span>
                </Label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    id="companyName"
                    name="companyName"
                    type="text"
                    value={formData.companyName}
                    onChange={handleChange}
                    placeholder="Your company or firm name"
                    className={`${inputClass} h-9 text-sm`}
                    data-testid="register-company-input"
                  />
                </div>
              </div>

              {/* Mobile */}
              <div>
                <Label htmlFor="mobile" className="text-slate-300 mb-1 block text-sm">
                  Mobile Number <span className="text-red-400">*</span>
                </Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    id="mobile"
                    name="mobile"
                    type="tel"
                    required
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder="Enter your mobile number"
                    className={`${inputClass} h-9 text-sm`}
                    data-testid="register-mobile-input"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <Label htmlFor="password" className="text-slate-300 mb-1 block text-sm">
                  Create Password <span className="text-red-400">*</span>
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Min 8 characters"
                    className={`${inputClass} h-9 text-sm`}
                    data-testid="register-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <Label htmlFor="confirmPassword" className="text-slate-300 mb-1 block text-sm">
                  Confirm Password <span className="text-red-400">*</span>
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm your password"
                    className={`${inputClass} h-9 text-sm`}
                    data-testid="register-confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Info note */}
              <div className="p-2 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                <p className="text-amber-400 text-xs">
                  ℹ️ Your account will need admin approval before you can add clients.
                </p>
              </div>

              {/* Terms & Conditions Checkbox */}
              <div className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                <Checkbox
                  id="register-terms-checkbox"
                  checked={termsAccepted}
                  onCheckedChange={setTermsAccepted}
                  className="mt-0.5 border-slate-500 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                  data-testid="register-terms-checkbox"
                />
                <label htmlFor="register-terms-checkbox" className="text-sm text-slate-300 cursor-pointer select-none">
                  By clicking Register, you agree to our{' '}
                  <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:text-emerald-300 underline">
                    Terms & Conditions
                  </a>
                  {' '}&{' '}
                  <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:text-emerald-300 underline">
                    Privacy Policy
                  </a>
                </label>
              </div>

              <button
                type="button"
                onClick={() => {
                  setLoginStep('email');
                  setFormData({ email: '', password: '', confirmPassword: '', name: '', mobile: '', companyName: '' });
                  setTermsAccepted(false);
                }}
                className="text-xs text-amber-400 hover:text-amber-300 transition-colors flex items-center gap-1"
              >
                <ArrowLeft className="w-3 h-3" /> Back to login
              </button>
            </>
          )}

          {/* Step: Email Verification Pending */}
          {loginStep === 'verification_pending' && (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto">
                <Mail className="w-8 h-8 text-emerald-400" />
              </div>
              
              <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                <p className="text-emerald-400 font-medium mb-2">Check Your Email!</p>
                <p className="text-slate-400 text-sm">
                  We have sent a verification link to <span className="text-white font-medium">{partnerData?.email}</span>.
                </p>
                <p className="text-slate-500 text-xs mt-2">
                  Click the link in the email to verify your account and start using the platform.
                </p>
              </div>
              
              <div className="bg-slate-800/50 rounded-lg p-4 text-left space-y-2">
                <p className="text-slate-300 text-sm font-medium">Did not receive the email?</p>
                <ul className="text-slate-400 text-xs space-y-1 list-disc list-inside">
                  <li>Check your spam or junk folder</li>
                  <li>Make sure <span className="text-white">{partnerData?.email}</span> is correct</li>
                  <li>Wait a few minutes for delivery</li>
                </ul>
              </div>
              
              <Button
                type="button"
                onClick={handleResendVerification}
                disabled={isLoading}
                variant="outline"
                className="w-full border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                data-testid="resend-verification-btn"
              >
                {isLoading ? 'Sending...' : 'Resend Verification Email'}
              </Button>
              
              <button
                type="button"
                onClick={() => {
                  setLoginStep('email');
                  setFormData({ email: '', password: '', confirmPassword: '', name: '', mobile: '', companyName: '' });
                  setPartnerData(null);
                }}
                className="text-xs text-slate-500 hover:text-slate-300 transition-colors"
              >
                ← Back to login
              </button>
            </div>
          )}

          {loginStep !== 'verification_pending' && loginStep !== 'audience' && (
            <Button
              type="submit"
              disabled={isLoading || ((loginStep === 'set_password' || loginStep === 'register_partner') && !termsAccepted)}
              className={`w-full font-semibold py-5 rounded-xl shadow-lg transition-all duration-300 disabled:opacity-50 ${
                (loginStep === 'set_password' || loginStep === 'register_partner') && !termsAccepted
                  ? 'bg-slate-700 text-slate-500 cursor-not-allowed shadow-none'
                  : 'bg-amber-500 hover:bg-amber-400 text-slate-900 shadow-amber-500/25 hover:shadow-amber-400/30'
              }`}
              data-testid="login-submit-btn"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  {getButtonText()}
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  {getButtonText()}
                  <ArrowRight className="w-5 h-5" />
                </span>
              )}
            </Button>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LoginModal;
